import { Component, ElementRef, Input, OnDestroy, Optional, Self, ViewChild, ViewChildren } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldControl } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime, Subject } from 'rxjs';

export interface SearchableDropdownElement {
  id: number;
  name: string;
}

@Component({
  selector: 'app-searchable-dropdown',
  standalone: true,
  imports: [
    MatOptionModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatIconModule,
    TranslateModule
  ],
  templateUrl: './searchable-dropdown.component.html',
  styleUrl: './searchable-dropdown.component.scss',
  providers: [
    { provide: MatFormFieldControl, useExisting: SearchableDropdownComponent }
  ],
})
export class SearchableDropdownComponent implements MatFormFieldControl<SearchableDropdownElement[]>, OnDestroy,
  ControlValueAccessor {

  @Input() filter: (searchString: string) => Promise<SearchableDropdownElement[]>;
  @Input() capacity?: number;
  @ViewChild('input') input: ElementRef;
  @ViewChildren(MatAutocompleteTrigger) autocompleteTrigger: MatAutocompleteTrigger[];

  filtered: SearchableDropdownElement[] = [];

  value: SearchableDropdownElement[] = [];
  stateChanges: Subject<void> = new Subject<void>();
  id: string;
  placeholder: string;
  focused = false;
  empty = true;
  shouldLabelFloat = true;
  required: boolean;
  disabled: boolean;
  errorState: boolean;
  controlType?: string;
  autofilled?: boolean;
  userAriaDescribedBy?: string;

  inputChanges = new Subject<string>();

  constructor(@Optional() @Self() public ngControl: NgControl) {

    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }

    this.inputChanges
      .pipe(debounceTime(400))
      .subscribe(async v => {
        let filtered = await this.filter(v);
        this.filtered = this.removeSelected(filtered);
      })
  }

  private removeSelected(values: SearchableDropdownElement[]): SearchableDropdownElement[] {
    return values.filter(e => !this.value.map(v => v.id).includes(e.id));
  }

  onChange = (_: any) => this.stateChanges.next();
  onTouched = () => {};

  setDescribedByIds(_: string[]): void {}

  onContainerClick(_: MouseEvent): void {}

  ngOnDestroy() {
    this.stateChanges.complete();
  }

  private get isCapacityReached(): boolean {
    return this.value.length >= this.capacity;
  }

  onElementSelected(element: SearchableDropdownElement): void {
    const value = [...this.value];
    value.push(element);
    this.value = value;
    this.clear();
    this.onChange(this.value);
  }

  onFocusOut() {
    this.focused = false;
    this.onTouched();
    this.stateChanges.next();
  }

  onFocusIn() {
    if (this.focused) {
      return;
    }

    this.focused = true;
    this.stateChanges.next();
  }

  async onInput(value: string): Promise<void> {

    if (value.trim() === '') {
      this.filtered = [];
      this.empty = true;
      this.autocompleteTrigger.forEach(element => element.closePanel());
      return;
    }

    this.empty = false;
    this.inputChanges.next(value);
  }

  onElementRemoved(element: SearchableDropdownElement): void {
    this.value = this.value.filter(e => e !== element);
    this.clear();
    this.onChange(this.value);
  }

  writeValue(obj: any): void {

    if (!obj) {
      obj = [];
    }

    this.value = obj;

    if (this.isCapacityReached) {
      this.disabled = true;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  clear(): void {
    this.filtered = [];
    this.input.nativeElement.blur();
    this.input.nativeElement.value = '';
  }
}
